/**
 * Player Performance Trend Utilities
 *
 * Calculates and formats player performance trends compared to season averages.
 * Used for heat map visualization and trend indicators in box scores.
 */

/**
 * Calculate trend direction for a stat compared to season average
 * @param {number|undefined} gameStat - Stat value from this game
 * @param {number|undefined} seasonTotal - Total stat for the season
 * @param {number|undefined} gamesPlayed - Games played this season
 * @param {number} threshold - Percentage threshold for "significant" difference (default 15%)
 * @returns {'up'|'down'|'neutral'} Trend direction
 */
export function calculateTrend(gameStat, seasonTotal, gamesPlayed, threshold = 15) {
  // Handle missing data
  if (gameStat === undefined || seasonTotal === undefined ||
      gamesPlayed === undefined || gamesPlayed === 0) {
    return 'neutral';
  }

  const seasonAvg = seasonTotal / gamesPlayed;

  // Avoid division by zero
  if (seasonAvg === 0) {
    return gameStat > 0 ? 'up' : 'neutral';
  }

  const percentDiff = ((gameStat - seasonAvg) / seasonAvg) * 100;

  if (percentDiff > threshold) return 'up';
  if (percentDiff < -threshold) return 'down';
  return 'neutral';
}

/**
 * Calculate overall heat level based on multiple stats
 * Uses weighted average of key stats (sport-specific)
 * @param {Object} player - Player stat object with game and season data
 * @param {'nba'|'nfl'|'mlb'|'nhl'} sport - Sport type
 * @returns {'hot'|'cold'|'neutral'} Heat level
 */
export function calculateOverallHeat(player, sport = 'nba') {
  let stats = [];
  const gamesPlayed = player.season_games_played || 1;

  // Sport-specific stat configurations
  if (sport === 'nba') {
    stats = [
      { game: player.points, season: player.season_points, weight: 3 },
      { game: player.rebounds, season: player.season_rebounds, weight: 1.5 },
      { game: player.assists, season: player.season_assists, weight: 2 },
      { game: player.steals, season: player.season_steals, weight: 1 },
      { game: player.blocks, season: player.season_blocks, weight: 1 }
    ];
  } else if (sport === 'nfl') {
    // For NFL, prioritize yards and TDs across categories
    stats = [
      { game: player.yards, season: player.season_yards, weight: 3 },
      { game: player.touchdowns, season: player.season_touchdowns, weight: 3 },
      { game: player.receptions, season: player.season_receptions, weight: 2 },
      { game: player.completions, season: player.season_completions, weight: 1.5 }
    ];
  } else if (sport === 'mlb') {
    // For MLB, prioritize hits, HRs, RBIs for batters
    stats = [
      { game: player.hits, season: player.season_hits, weight: 2.5 },
      { game: player.home_runs, season: player.season_home_runs, weight: 3 },
      { game: player.rbi, season: player.season_rbi, weight: 2 },
      { game: player.runs, season: player.season_runs, weight: 1.5 },
      { game: player.strikeouts, season: player.season_strikeouts, weight: 2, inverse: true } // For pitchers
    ];
  } else if (sport === 'nhl') {
    stats = [
      { game: player.goals, season: player.season_goals, weight: 3 },
      { game: player.assists, season: player.season_assists, weight: 2 },
      { game: player.points, season: player.season_points, weight: 2.5 },
      { game: player.shots, season: player.season_shots, weight: 1 }
    ];
  }

  let totalWeight = 0;
  let weightedSum = 0;

  stats.forEach(stat => {
    if (stat.game !== undefined && stat.season !== undefined && stat.season > 0) {
      const avg = stat.season / gamesPlayed;
      let diff = ((stat.game - avg) / avg) * 100;

      // For inverse stats (like strikeouts for pitchers), flip the sign
      if (stat.inverse) {
        diff = -diff;
      }

      weightedSum += diff * stat.weight;
      totalWeight += stat.weight;
    }
  });

  if (totalWeight === 0) return 'neutral';

  const averageDiff = weightedSum / totalWeight;

  // Thresholds for heat classification
  if (averageDiff > 20) return 'hot';   // 20%+ above average
  if (averageDiff < -20) return 'cold'; // 20%+ below average
  return 'neutral';
}

/**
 * Get season average for a stat
 * @param {number|undefined} seasonTotal - Season total
 * @param {number|undefined} gamesPlayed - Games played
 * @returns {number|null} Average or null if cannot calculate
 */
export function getSeasonAverage(seasonTotal, gamesPlayed) {
  // Guard against undefined, null, NaN, or zero
  if (seasonTotal == null || gamesPlayed == null || gamesPlayed === 0 ||
      isNaN(seasonTotal) || isNaN(gamesPlayed)) {
    return null;
  }
  const avg = seasonTotal / gamesPlayed;
  return isNaN(avg) ? null : avg;
}

/**
 * Format stat value for display (rounds to 1 decimal)
 * @param {number|null} value - Value to format
 * @returns {string} Formatted value
 */
export function formatStatValue(value) {
  if (value === null || value === undefined) return '-';
  return value.toFixed(1);
}

/**
 * Get performance emoji based on heat level
 * @param {'hot'|'cold'|'neutral'} heat - Heat level
 * @returns {string} Emoji
 */
export function getPerformanceEmoji(heat) {
  switch (heat) {
    case 'hot':
      return '🔥';
    case 'cold':
      return '❄️';
    default:
      return '';
  }
}
